import stateCitiesData from '@/data/Content/StoreLocator/storeLocatorStateCitiesList.json';
import Cookies from 'cookies';
import { GetServerSidePropsContext } from 'next';
import {
	DEFAULT_STATE_ID,
	DEFAULT_STORE_ID,
	GEO_LOCATION_COOKIE_NAME,
	STORE_CODE_SEARCH_PARAM,
} from 'shared/custom/constants/commonConstants';
import { getStoreIdAndStateId } from 'shared/custom/utils/cookieHandler';

// Types
export type StoreLocation = {
	stLocId: string;
	stateId: string;
};
export type ResolvedStoreLocation = StoreLocation & {
	hasPreviousAnswer: boolean;
};

// Helpers
export const findStoreLocationByPhysicalStoreId = (
	physicalStoreId?: string
): StoreLocation | null => {
	if (!physicalStoreId) return null;

	const storeData = stateCitiesData.formScheduleObject
		.flatMap((stateStoreData) =>
			stateStoreData.stores.map((store) => ({
				...store,
				stateId: stateStoreData.stateId,
			}))
		)
		.find((store) => store.stLocId === physicalStoreId);

	if (!storeData) return null;

	return {
		stLocId: storeData.stLocId,
		stateId: storeData.stateId,
	};
};
// Query Helpers
export const getStoreIdFromQuery = (context: GetServerSidePropsContext): string | null => {
	const queryParams = context.query;

	const storeId =
		typeof queryParams[STORE_CODE_SEARCH_PARAM] === 'string'
			? queryParams[STORE_CODE_SEARCH_PARAM]
			: null;

	return storeId;
};

export const resolveStoreFromQuery = (context: GetServerSidePropsContext): StoreLocation | null => {
	const storeId = getStoreIdFromQuery(context);

	return findStoreLocationByPhysicalStoreId(storeId ?? undefined);
};

/**
 * Resolves the active store location for the current SSR request.
 *
 * Priority order:
 * 1. `store_code` query parameter (highest priority)
 * 2. GEO_LOCATION_COOKIE_NAME cookie
 * 3. Default store (fallback)
 */
export const resolveStoreLocation = (context: GetServerSidePropsContext): ResolvedStoreLocation => {
	const storeFromSearchParam = resolveStoreFromQuery(context);

	if (storeFromSearchParam) {
		return {
			...storeFromSearchParam,
			hasPreviousAnswer: true,
		};
	}

	const cookies = new Cookies(context.req, context.res);
	const cookieValue = cookies.get(GEO_LOCATION_COOKIE_NAME) ?? 'defaultStore';

	if (cookieValue) {
		const storeFromCookie = getStoreIdAndStateId(cookieValue);

		return {
			stLocId: storeFromCookie.stLocId,
			stateId: storeFromCookie.stateId,
			hasPreviousAnswer: storeFromCookie.hasPreviousAnswer,
		};
	}

	return {
		stLocId: DEFAULT_STORE_ID,
		stateId: DEFAULT_STATE_ID,
		hasPreviousAnswer: false,
	};
};
